import Axios, { AxiosError } from "axios";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { caseKeys } from "src/query-keys";
import { APIError, RegularGrid, LineBasedGrid, PointBasedGrid } from "src/types";

export interface RegularGridMutationData {
  xcount: number;
  ycount: number;
  distance: number;
}

export interface LineBasedGridMutationData {
  distance: number;
  count: number;
  minDistance: number;
  maxDistance: number;
  tolerance: number;
}

export interface PointBasedGridMutationData {
  numberOfCircles: number;
  pointsPerCircle: number;
  minDistance: number;
  maxDistance: number;
  tolerance: number;
}

/**
 * Deserialize the json format into a Javascript object
 */
function deserialize(data: LineBasedGrid): LineBasedGrid | RegularGrid | PointBasedGrid {
  return { ...data };
}

/**
 * Regular grid
 */
export function useRegularGrid(caseId: string | number) {
  async function queryFn(caseId: number | string): Promise<RegularGrid> {
    const response = await Axios.get(`/planning-api/cases/${caseId}/regularGrid`);
    return deserialize(response.data) as RegularGrid;
  }
  return useQuery<RegularGrid, AxiosError<APIError[]>>(caseKeys.regularGrid(caseId), () => queryFn(caseId));
}
export function useRegularGridUpdateMutation(caseId: number | string) {
  const queryClient = useQueryClient();

  async function mutationFn(caseId: number | string, data: RegularGridMutationData): Promise<void> {
    return Axios.put(`/planning-api/cases/${caseId}/regularGrid/`, data);
  }

  return useMutation<void, AxiosError<APIError[]>, RegularGridMutationData, unknown>(
    (values: RegularGridMutationData) => mutationFn(caseId, values),
    {
      onSuccess: () => queryClient.invalidateQueries(caseKeys.regularGrid(caseId)),
    }
  );
}

/**
 * Line based grid
 */
export function useLineBasedGrid(caseId: string | number) {
  async function queryFn(caseId: number | string): Promise<LineBasedGrid> {
    const result = await Axios.get(`/planning-api/cases/${caseId}/lineBasedGrid`);
    return deserialize(result.data) as LineBasedGrid;
  }

  return useQuery<LineBasedGrid, AxiosError<APIError>>(caseKeys.lineBasedGrid(caseId), () => queryFn(caseId));
}

export function useLineBasedGridUpdateMutation(caseId: number | string) {
  const queryClient = useQueryClient();

  async function mutationFn(caseId: number | string, data: LineBasedGridMutationData): Promise<void> {
    return Axios.put(`/planning-api/cases/${caseId}/lineBasedGrid/`, data);
  }

  return useMutation<void, AxiosError<APIError[]>, LineBasedGridMutationData, unknown>(
    (values: LineBasedGridMutationData) => mutationFn(caseId, values),
    {
      onSuccess: () => queryClient.invalidateQueries(caseKeys.lineBasedGrid(caseId)),
    }
  );
}

/**
 * Point based grid
 */
export function usePointBasedGrid(caseId: string | number) {
  async function queryFn(caseId: number | string): Promise<PointBasedGrid> {
    const response = await Axios.get(`/planning-api/cases/${caseId}/pointBasedGrid`);
    return deserialize(response.data) as PointBasedGrid;
  }
  return useQuery<PointBasedGrid, AxiosError<APIError>>(caseKeys.pointBasedGrid(caseId), () => queryFn(caseId));
}
export function usePointBasedGridUpdateMutation(caseId: number | string) {
  const queryClient = useQueryClient();

  async function mutationFn(caseId: number | string, data: PointBasedGridMutationData): Promise<void> {
    return Axios.put(`/planning-api/cases/${caseId}/pointBasedGrid/`, data);
  }

  return useMutation<void, AxiosError<APIError[]>, PointBasedGridMutationData, unknown>(
    (values: PointBasedGridMutationData) => mutationFn(caseId, values),
    {
      onSuccess: () => queryClient.invalidateQueries(caseKeys.pointBasedGrid(caseId)),
    }
  );
}
